import isEqual from "lodash/isEqual";
import useEventCallback from "hooks/useEventCallback";
import useAreaChart from "../../AreaChartProvider/useAreaChartStore";
import useAreaChartActions from "../../AreaChartProvider/useAreaChartActions";
import useDrawingActions from "../../DrawingProvider/useDrawingActions";
import type { Points } from "../../TooltipProvider";
import useTooltip from "../../TooltipProvider/useTooltipStore";
import useXAxisActions from "../../XAxisProvider/useXAxisActions";
import useYAxisActions from "../../YAxisProvider/useYAxisActions";

/** Opens the tooltip and cursor on every series' datum at an x-axis date; stable so handlers built on it are too. */
function useShowPointsTooltip() {
  const { series, margin } = useAreaChart();
  const { tooltipData, showTooltip } = useTooltip();
  const { getYScale } = useYAxisActions();
  const { getXScale } = useXAxisActions();
  const { xAccessor, yAccessor } = useAreaChartActions();
  const { handleSetCoordinates } = useDrawingActions();

  return useEventCallback((date: Date, index: number) => {
    const points = { coordinates: [], payload: [] } as Points<unknown>;
    const x = getXScale();

    series.forEach((item) => {
      const y = getYScale(item.yAxisId);

      const datum = item.data.find((d) => String(xAccessor(d)) === String(date));
      if (!datum) return;

      const xValue = x(xAccessor(datum)) ?? 0;
      const yValue = y(yAccessor(datum)) ?? 0;

      points.payload.push({
        color: item.color,
        datum: datum,
        index: index,
      });
      points.coordinates.push({
        datum: datum,
        x: xValue,
        y: yValue,
        color: item.color,
      });
      points.label = String(xAccessor(datum));
    });

    if (points.payload.length === 0) return;
    const currentCoordinates = tooltipData?.coordinates || [];
    if (isEqual(points.coordinates, currentCoordinates)) return;

    handleSetCoordinates(points.coordinates);

    // Anchored to the date rather than the pointer, so the tooltip stays beside the cursor line.
    showTooltip({
      tooltipData: points,
      tooltipLeft: x(date),
      tooltipTop: margin.top,
    });
  });
}

export default useShowPointsTooltip;
